import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RawMaterialDetailsFormService } from './raw-material-details-form.service';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { ToolbarEventsContextService } from 'src/app/core/services/toolbar-events/toolbar-events-context.service';
import {
  ConfigurationManagerService,
  ConnectionService,
  EntityManagerService,
  LoaderService,
  ToolbarEventsService,
  UserActiveSessionManagerService
} from 'src/app/core';
import { AlertDialogService, ConfirmDialogService, GenericJsonDetailDialogService } from 'src/app/dialogs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { $state } from 'app/ajs-upgraded-providers';
import { RawMaterials, Suppliers } from 'tilby-models';
import { map, Observable, of, startWith, Subject, takeUntil } from 'rxjs';
import { RawMaterialsFormGroups } from '../raw-materials.model';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TrimInputDirective } from 'src/app/directives/trim.directive';

@Component({
  selector: 'app-raw-material-details',
  standalone: true,
  imports: [ MatFormFieldModule, ReactiveFormsModule,
    TranslateModule, FormsModule, CommonModule,
    MatInputModule, MatButtonModule, MatSlideToggleModule,
    MatIconModule, MatAutocompleteModule, TrimInputDirective
  ],
  templateUrl: './raw-material-details.component.html',
  styleUrl: './raw-material-details.component.scss'
})
export class RawMaterialDetailsComponent implements OnInit, OnDestroy {

  readonly rawMaterialFormService = inject(RawMaterialDetailsFormService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly connection = inject(ConnectionService);
  private readonly entityManager = inject(EntityManagerService);
  private readonly genericJsonDetailDialog = inject(GenericJsonDetailDialogService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly loaderService = inject(LoaderService);
  private readonly state = inject($state);
  private readonly toolbarEventsContextService = inject(ToolbarEventsContextService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly translate = inject(TranslateService);
  private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);
  
  private areEditsBlocked = this.configurationManagerService.isMagoEnabled() && this.userActiveSessionManagerService.getSession()?.role !== 'root';
  protected readonly hasToolbarMenu = this.configurationManagerService.isUserPermittedOptIn("show_developer_options");
  protected filteredSuppliers: Observable<Suppliers[] | undefined> | undefined = of([]);

  @Input() rawMaterial!: RawMaterials;
  rawMaterialsList: RawMaterials[] = [];
  suppliersList: Array<Suppliers> = [];
  
  private destroy$ = new Subject<void>(); 

  isNew: boolean = false;
  rawMaterialForm!: RawMaterialsFormGroups;

  isDataListReady = this.itemDataCatalogService.isReady;
  rawMaterialFormReady = this.rawMaterialFormService.rawMaterialFormReady;
  
  constructor() {
    effect(() => {
      if(this.isDataListReady()) {
        this.rawMaterialsList = this.itemDataCatalogService.getDataByKey('raw_materials') || [];
        this.suppliersList = this.itemDataCatalogService.getDataByKey('suppliers') || [];
        this.rawMaterialForm = this.rawMaterialFormService.createForm(this.rawMaterial);
        this.loaderService.forceHide();
        this.listenToSupplierSearch();
      }
    }, { allowSignalWrites: true })

    effect(() => {
      if(this.rawMaterialFormReady()) {
        this.listenToRawMaterialNameChanges();
      }
    })
  }

  ngOnInit(): void {
    this.isNew = !this.rawMaterial?.id;
    this.createToolbarButtons();
    this.initContextToolbarLabel();
  }

  initContextToolbarLabel() {
    if(this.isNew) {
      this.toolbarEventsContextService.label = `ITEMS.RAW_MATERIALS.DETAILS.NAME_PLACEHOLDER`;
    } else {
      this.toolbarEventsContextService.label = ( this.rawMaterial.name || "" ) + '\u200b';
    }
  }
  
  private listenToRawMaterialNameChanges() {
    this.rawMaterialFormService.name?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
      this.toolbarEventsContextService.label = value? value + '\u200b': `ITEMS.RAW_MATERIALS.DETAILS.NAME_PLACEHOLDER`;
    })
  }

    
  displaySupplierName = (value?: Suppliers | number): string => {
    let name = '';
    if(typeof value === 'object') name = value?.name;
    else if(typeof value === 'number') {
      const sup = this.suppliersList.find(sup => sup.id === value);
      if(sup) name = sup.name;
    }
    return name;
  }

  listenToSupplierSearch() {
    this.filteredSuppliers = this.rawMaterialFormService.default_supplier_id?.valueChanges.pipe(
        startWith(this.rawMaterialFormService.default_supplier_id?.value || null),
        map((value: any) => 
          this._filterSuppliers(value)
        )
    );
  }

  private _filterSuppliers(value: string | number): Suppliers[] {
    if(!this.suppliersList) {
      return [];
    }

    if(typeof value === 'number') {
      const supplier = this.suppliersList.find(sup => sup.id === value);
      return supplier? [supplier]: [];
    }

    const filterValue = typeof value === 'string'? value.toLocaleLowerCase().trim(): '';
    return this.suppliersList?.filter(option => option.name.toLocaleLowerCase().includes(filterValue));
  }

  onToggleStockType($event: MatSlideToggleChange) {
    if($event.checked) {
      this.rawMaterialFormService.stock_type?.setValue('simple', {emitEvent: false});
      this.rawMaterialFormService.auto_unload?.setValue(true, {emitEvent: false});
    } else {
      this.rawMaterialFormService.stock_type?.setValue(null, {emitEvent: false});
      this.rawMaterialFormService.auto_unload?.setValue(false, {emitEvent: false});
    }
  }
  
  /**
   * Create buttons for the toolbar
   */
  createToolbarButtons = () => {
    this.toolbarEventsContextService.backButton$.next({
        isIt: signal(true),
        name: 'arrow_back',
        icon: signal('arrow_back'),
        click: () => this.back()
    });

    this.toolbarEventsContextService.buttons$.next({
        barButtons: [
            {
                isIt: () => true,
                name: 'save',
                icon: signal('check'),
                click: _ => {
                  this.save();
                }
            },
            {
                isIt: () => true,
                name: 'settings_backup_restore',
                icon: signal('settings_backup_restore'),
                click: _ => {
                  this.restore();
                }
            },
        ],
        panelButtons: []
    });

    this.toolbarEventsService.buttons$.next({
      barButtons: [
        {
          isIt: () => this.connection.isOffline(),
          name: 'cloud_off',
          icon: signal('cloud_off'),
          click: _ => null
        }
      ],
      panelButtons: [
        {
          isIt: signal(this.hasToolbarMenu),
          name: 'show_json_raw_material',
          icon: signal('data_object'),
          label: signal('ITEMS.RAW_MATERIALS.ACTIONS.SHOW_JSON'),
          click: _ => this.openRawMaterialJsonDetail()
        }
      ]
    })
  }
  
  async openRawMaterialJsonDetail() {
    await this.genericJsonDetailDialog.openDialog(
      { data: 
        { title:'ITEMS.RAW_MATERIALS.ACTIONS.SHOW_JSON', 
        nameFile: 'item_json_data', 
        json: this.rawMaterialFormService.getRawMaterialFields(this.rawMaterial) } 
    });
  }
  
  back() {
    this.state.go(`app.new.items.raw-materials`);
    this.toolbarEventsContextService.buttons$.next({panelButtons: [], barButtons: []});
  }

  async restore() {
    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.RAW_MATERIALS.DETAILS.UNDO_CHANGES',
      }
    });

    if(!confirm) return;
    
    this.rawMaterialFormService.resetFormValues(this.rawMaterial);
  }

  async save() {
    if(this.areEditsBlocked) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED')
        }})
      return;
    }

    if(this.rawMaterialFormService.form.invalid) {
      this.rawMaterialFormService.form.markAllAsTouched();
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('FORM_ERRORS.THERE_ARE_ERRORS')
        }})
      return;
    }

    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.RAW_MATERIALS.DETAILS.WANT_TO_SAVE',
      }
    });

    if(!confirm) return;

    this.loaderService.show();

    const rawMaterialToSend = {
      ...this.rawMaterialFormService.getRawMaterialFields(this.rawMaterial),
      ...(this.rawMaterial?.id && {id: this.rawMaterial.id})
    } as RawMaterials;

    try {
      if(rawMaterialToSend.id) {
        this.rawMaterial = await this.entityManager.rawMaterials.putOneOnline(rawMaterialToSend);
      } else {
        this.rawMaterial = await this.entityManager.rawMaterials.postOneOnline(rawMaterialToSend);
      }

      // Aggiornamento lista materie prime
      // const currentRawMaterials = this.itemDataCatalogService.getDataByKey('raw-materials') || [];

      // if (rawMaterialToSend.id) {
      //   const index = currentRawMaterials.findIndex(r => r.id === this.rawMaterial.id);
      //   if (index !== -1) {
      //     currentRawMaterials[index] = this.rawMaterial;
      //   }
      // } else {
      //   currentRawMaterials.push(this.rawMaterial);
      // }

      // this.itemDataCatalogService.addDataToCatalog('raw-materials', currentRawMaterials);

      this.state.go(`app.new.items.raw-materials`);

      this._snackBar.open(this.translate.instant('ITEMS.RAW_MATERIALS.SAVED'), undefined, { 
        duration: 3000, 
        horizontalPosition: 'start', 
        verticalPosition: "bottom", 
      });
    } catch (e) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: rawMaterialToSend.id ? 'ITEMS.RAW_MATERIALS.ERROR_IN_EDIT' : 'ITEMS.RAW_MATERIALS.ERROR_IN_CREATION'
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.rawMaterialFormReady.set(false);
  }
}
